import React from "react";
import { Paper, Typography, Box, Button, Chip, Stack, Tooltip } from "@mui/material";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import InsertDriveFileIcon from "@mui/icons-material/InsertDriveFile";
import DeleteIcon from "@mui/icons-material/Delete";

/**
 * A reusable file upload component with drag and drop functionality
 * 
 * @param {Object} props - Component props
 * @param {Function} props.onFileSelection - Function called when files are selected
 * @param {Function} props.onUpload - Function called when upload button is clicked
 * @param {boolean} props.disabled - Whether the upload is disabled
 * @param {boolean} props.modelSelected - Whether a model is selected in the dropdown
 * @param {string} props.maxRecords - Maximum number of records
 * @param {Object} props.theme - Theme object
 * @param {string} props.acceptedFormats - Accepted file formats (e.g., ".xlsx")
 * @param {Object} props.sx - Additional styles for Paper component
 * @param {File} props.selectedFile - Currently selected file
 * @param {Function} props.onClearFile - Function to clear the selected file
 * @param {boolean} props.isUploading - Whether file is currently uploading
 */
const CUpload = ({
  onFileSelection,
  onUpload,
  disabled = false,
  modelSelected = false,
  maxRecords = "25,000",
  theme,
  acceptedFormats = ".xlsx",
  sx = {},
  selectedFile = null,
  onClearFile,
  isUploading = false,
}) => {
  const uploadEnabled = modelSelected && !disabled;

  const handleDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (uploadEnabled && e.dataTransfer.files && e.dataTransfer.files.length > 0) {
      onFileSelection(e.dataTransfer.files);
    }
  };

  const handleClearFile = () => {
    if (onClearFile) {
      onClearFile();
    }
  };

  const handleFileClick = () => {
    if (uploadEnabled && !isUploading) {
      document.getElementById("change-file-button").click();
    }
  };

  const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  return (
    <Paper
      variant="outlined"
      onDragOver={handleDragOver}
      onDrop={handleDrop}
      sx={{
        p: 2.5,
        textAlign: "center",
        borderStyle: "dashed",
        mb: 2,
        bgcolor: selectedFile
          ? "rgba(76, 175, 80, 0.05)"
          : "rgba(255, 152, 0, 0.05)",
        borderColor: selectedFile
          ? theme.palette.success.main
          : uploadEnabled ? theme.palette.primary.main : theme.palette.action.disabled,
        borderWidth: 2,
        "&:hover": {
          borderColor: selectedFile
            ? theme.palette.success.light
            : uploadEnabled ? theme.palette.primary.light : theme.palette.action.disabled,
          bgcolor: selectedFile
            ? "rgba(76, 175, 80, 0.1)"
            : uploadEnabled ? "rgba(255, 152, 0, 0.1)" : "rgba(0, 0, 0, 0.03)",
        },
        transition: "all 0.3s ease",
        opacity: uploadEnabled || selectedFile ? 1 : 0.7,
        ...sx,
      }}
    >
      {!selectedFile ? (
        // Upload State
        <>
          <CloudUploadIcon
            color={uploadEnabled ? "primary" : "disabled"}
            sx={{ fontSize: 48, mb: 1, mt: 0.5 }}
          />
          <Typography
            variant="h6"
            color={uploadEnabled ? "primary" : "text.disabled"}
            sx={{
              mb: 0.2,
              fontSize: "18px",
              fontWeight: 500
            }}
          >
            {uploadEnabled
              ? "Drag and drop your Excel file here or click to browse"
              : "Please select a model first to enable upload"}
          </Typography>

          <Typography
            variant="body2"
            color="textSecondary"
            sx={{ mb: 0.5 }}
          >
            Maximum {maxRecords} records
          </Typography>
          <Typography variant="caption" color="textSecondary">
            Only {acceptedFormats} files are supported. Please upload files
            without formulas for better processing.
          </Typography>

          <Box sx={{ mt: 1.5 }}>
            <input
              accept={acceptedFormats}
              style={{ display: "none" }}
              id="upload-file-button"
              type="file"
              onChange={(e) => onFileSelection(e.target.files)}
              disabled={!uploadEnabled}
            />
            <label htmlFor="upload-file-button">
              <Button
                variant="contained"
                component="span"
                color={uploadEnabled ? "primary" : "inherit"}
                startIcon={<CloudUploadIcon />}
                disabled={!uploadEnabled}
                size="small"
              >
                Choose File
              </Button>
            </label>
          </Box>
        </>
      ) : (
        // File Selected State
        <>
          <InsertDriveFileIcon
            color="success"
            sx={{ fontSize: 48, mb: 1 }}
          />
          <Typography variant="h6" gutterBottom color="success.main" sx={{ mb: 0.5 }}>
            File Selected
          </Typography>

          <Stack
            direction="column"
            spacing={0.5}
            alignItems="center"
            sx={{ mb: 1.5 }}
          >
            <Tooltip 
              title="Click to change file" 
              arrow
              placement="top"
            >
              <Chip
                icon={<InsertDriveFileIcon />}
                label={selectedFile.name}
                color="success"
                variant="outlined"
                size="small"
                onClick={handleFileClick}
                sx={{ 
                  cursor: uploadEnabled && !isUploading ? 'pointer' : 'default',
                  '&:hover': {
                    backgroundColor: uploadEnabled && !isUploading ? 'rgba(76, 175, 80, 0.1)' : 'transparent'
                  }
                }}
              />
            </Tooltip>
            <Typography variant="body2" color="textSecondary">
              {formatFileSize(selectedFile.size)}
            </Typography>
          </Stack>

          <input
            accept={acceptedFormats}
            style={{ display: "none" }}
            id="change-file-button"
            type="file"
            onChange={(e) => onFileSelection(e.target.files)}
            disabled={!uploadEnabled || isUploading}
          />

          <Box sx={{ display: 'flex', justifyContent: 'center', gap: 1.5 }}>
            <Button
              variant="outlined"
              color="error"
              startIcon={<DeleteIcon />}
              onClick={handleClearFile}
              disabled={isUploading}
              size="small"
            >
              Remove
            </Button>
            
            {onUpload && (
              <Button
                variant="contained"
                color="primary"
                startIcon={isUploading ? null : <CloudUploadIcon />}
                onClick={onUpload}
                disabled={!uploadEnabled || isUploading}
                size="small"
              >
                {isUploading ? "Uploading..." : "Upload"}
              </Button>
            )}
          </Box>
        </>
      )}
    </Paper>
  );
};

export default CUpload;